import { useEffect, useState } from "react";
import { getYggEvaluation, submitYggManagerEval } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { toast } from "sonner";
import { Save, Loader2, CheckCircle2, ShieldCheck, TrendingUp, Heart, Clock, Star, FileText, AlertCircle } from "lucide-react";

const COMPETENCIES = [
  { key: "musteri_odakliligi", label: "Müşteri Odaklılık" },
  { key: "analitik_dusunme", label: "Analitik Düşünme" },
  { key: "iletisim_becerisi", label: "İletişim Becerisi" },
  { key: "takim_calismasina_yatkinlik", label: "Takım Çalışması" },
  { key: "ogrenme_ve_degisime_aciklik", label: "Öğrenme & Değişim" },
  { key: "sonuc_odakliligi", label: "Sonuç Odaklılık" },
  { key: "dijital_farkindalik", label: "Dijital Farkındalık" },
  { key: "kaynaklari_verimli_kullanma", label: "Kaynakları Verimli Kullanma" },
  { key: "geri_bildirim", label: "Geri Bildirim" },
  { key: "liderlik", label: "Liderlik" },
];
const SCALE = [{v:1,l:"Desteklenmeli"},{v:2,l:"Gelişime Açık"},{v:3,l:"Başarılı"},{v:4,l:"Çok Başarılı"},{v:5,l:"Mükemmel"}];
const RISK = ["düşük", "orta", "yüksek"];
const MOBILITY = ["hazır", "şartlı", "hayır"];
const LETTERS = ["A", "B", "C", "D"];
const TIMING = ["kısa vade", "orta vade", "uzun vade"];
const CODE_LABELS = {
  terfi_edebilir: "Terfi Edebilir", mevcut_rolde_devam: "Mevcut Rolde Devam",
  alaninin_uzmani: "Alanının Uzmanı", anahtar_calisan: "Anahtar Çalışan", tespit_icin_erken: "Tespit İçin Erken",
};

export default function YggManagerEvalForm({ cycleId, employeeId, employeeName }) {
  const [ev, setEv] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({});

  useEffect(() => {
    if (!cycleId) return;
    getYggEvaluation(cycleId, employeeId).then((e) => {
      setEv(e.manager_eval || { competencies: {} });
      setSurvey(e.survey || null);
      setCtx(e.employee_context || null);
      setHistory(e.history || []);
      setStatus(e.stage_status || {});
    });
  }, [cycleId, employeeId]);

  const update = (patch) => setEv({ ...ev, ...patch });
  const setComp = (k, v) => setEv({ ...ev, competencies: { ...(ev.competencies || {}), [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await submitYggManagerEval(cycleId, employeeId, ev);
      toast.success(`${employeeName} için değerlendirme kaydedildi.`);
      setStatus({ ...status, manager_eval: "completed" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kaydedilemedi";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!ev) return <div className="card-surface p-6 text-sm text-slate-400" data-testid="ygg-mgr-loading">Yükleniyor…</div>;
  const done = status.manager_eval === "completed";
  const surveyDone = status.survey === "completed" && survey;

  return (
    <div className="space-y-4" data-testid="ygg-manager-eval-form">
      {/* ===== Employee context header ===== */}
      <div className="card-surface p-5" data-testid="ygg-mgr-context">
        <div className="flex items-start gap-4">
          {ctx?.avatar && <img src={ctx.avatar} alt={employeeName} className="w-14 h-14 rounded-full object-cover ring-2 ring-sky-100" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-xl font-semibold">{employeeName}</h3>
              {ctx?.is_talent && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-100 text-violet-800"><Star className="w-3 h-3" /> Yetenek Havuzunda</span>}
              {done && <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Değerlendirme Tamamlandı</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {[ctx?.role_title, ctx?.department, ctx?.branch_name || ctx?.location].filter(Boolean).join(" · ")}
            </div>
            <div className="flex flex-wrap gap-2 mt-2.5">
              {ctx?.performance_rating != null && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-sky-50 text-sky-800 border border-sky-100" data-testid="ygg-ctx-performance">
                  <TrendingUp className="w-3.5 h-3.5" /> Performans {ctx.performance_rating}{ctx.performance_trend ? ` ${ctx.performance_trend}` : ""}
                </span>
              )}
              {ctx?.engagement_score != null && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-100" data-testid="ygg-ctx-engagement">
                  <Heart className="w-3.5 h-3.5" /> Bağlılık %{ctx.engagement_score}
                </span>
              )}
              {ctx?.tenure_years != null && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-50 text-slate-700 border border-slate-100">
                  <Clock className="w-3.5 h-3.5" /> Kıdem {Math.round(ctx.tenure_years * 10) / 10} yıl
                </span>
              )}
              {ctx?.potential && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-100">
                  Potansiyel: {ctx.potential === "yuksek" ? "Yüksek" : ctx.potential === "orta" ? "Orta" : ctx.potential}
                </span>
              )}
            </div>
          </div>
        </div>
        {history.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="ygg-mgr-history-chips">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Geçmiş YGG Sonuçları</div>
            <div className="flex flex-wrap gap-1.5">
              {history.map((h) => (
                <span key={h.cycle_year} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-800 border border-indigo-100">
                  <span className="font-bold">{h.cycle_year}</span> · {CODE_LABELS[h.talent_code] || h.talent_code}{h.pys_letter ? ` · PYS ${h.pys_letter}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* ===== Survey (read-only) ===== */}
        <div className="lg:col-span-2 lg:sticky lg:top-4">
          {surveyDone ? (
            <div className="card-surface p-5 border-l-4 border-l-sky-400" data-testid="ygg-mgr-survey-panel">
              <div className="label-eyebrow flex items-center gap-1.5 mb-3"><FileText className="w-3.5 h-3.5" /> Çalışanın Anket Cevapları</div>
              <div className="space-y-3 text-sm">
                <SurveyItem label="Kendini yetkin hissediyor mu?" value={survey.current_role_competency} />
                <SurveyChips label="Güçlü gördüğü yetkinlikler" items={survey.top3_strengths} tone="emerald" />
                <SurveyChips label="Gelişime açık gördüğü yetkinlikler" items={survey.top3_dev_areas} tone="amber" />
                <SurveyItem label="İhtiyaç duyduğu destekler" value={survey.support_needed} />
                <SurveyItem label="Dikey hareket hedefi" value={survey.vertical_target} />
                <SurveyItem label="Yatay hareket / rotasyon hedefi" value={survey.horizontal_target} />
                <SurveyItem label="Son 2 yıl projeleri" value={survey.projects_last_2y} />
                <div className="flex gap-4">
                  {survey.english_level && <div><div className="text-[11px] font-semibold text-slate-500 uppercase">İngilizce</div><div className="text-slate-800 font-medium">{survey.english_level}</div></div>}
                </div>
                <SurveyItem label="Diğer görüşleri" value={survey.additional_comments} />
              </div>
            </div>
          ) : (
            <div className="card-surface p-5 border-l-4 border-l-amber-400" data-testid="ygg-mgr-survey-missing">
              <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold"><AlertCircle className="w-4 h-4" /> Anket henüz doldurulmadı</div>
              <p className="text-xs text-slate-500 mt-1.5">{employeeName} öz-değerlendirme anketini tamamladığında cevapları burada göreceksin. Değerlendirmeni yine de kaydedebilirsin.</p>
            </div>
          )}
        </div>

        {/* ===== Evaluation form ===== */}
        <div className="card-surface p-6 space-y-5 lg:col-span-3">
          <div className="label-eyebrow flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Yönetici Değerlendirmesi</div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-2 block">10 Yetkinlik (1-5)</label>
            <div className="space-y-1.5">
              {COMPETENCIES.map((c) => (
                <div key={c.key} className="flex items-center gap-3" data-testid={`ygg-mgr-comp-${c.key}`}>
                  <span className="w-48 text-sm text-slate-700">{c.label}</span>
                  <div className="flex gap-1">
                    {SCALE.map((s) => {
                      const on = (ev.competencies || {})[c.key] === s.v;
                      return (
                        <button key={s.v} type="button"
                          onClick={() => setComp(c.key, s.v)}
                          title={s.l}
                          className={`w-9 h-8 rounded text-xs font-semibold border transition ${on ? "bg-sky-500 text-white border-sky-500" : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"}`}
                          data-testid={`ygg-mgr-comp-${c.key}-${s.v}`}
                        >{s.v}</button>
                      );
                    })}
                  </div>
                  <span className="text-[11px] text-slate-500 flex-1">{(ev.competencies || {})[c.key] ? SCALE.find(s => s.v === ev.competencies[c.key])?.l : "—"}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Güçlü Yönler</label>
              <Textarea rows={2} value={ev.strengths || ""} onChange={(e) => update({ strengths: e.target.value })} data-testid="ygg-mgr-strengths" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Gelişim Alanları</label>
              <Textarea rows={2} value={ev.dev_areas || ""} onChange={(e) => update({ dev_areas: e.target.value })} data-testid="ygg-mgr-devareas" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Row label="Mobilite">
              <Select value={ev.mobility || ""} onValueChange={(v) => update({ mobility: v })}>
                <SelectTrigger className="h-9 bg-white" data-testid="ygg-mgr-mobility"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent className="bg-white">{MOBILITY.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Risk Durumu">
              <Select value={ev.risk_status || ""} onValueChange={(v) => update({ risk_status: v })}>
                <SelectTrigger className="h-9 bg-white" data-testid="ygg-mgr-risk"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent className="bg-white">{RISK.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="PYS Harfi (Öncesi)">
              <Select value={ev.pys_letter_pre || ""} onValueChange={(v) => update({ pys_letter_pre: v })}>
                <SelectTrigger className="h-9 bg-white" data-testid="ygg-mgr-pys"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white">{LETTERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Aksiyon Zamanlaması">
              <Select value={ev.action_timing || ""} onValueChange={(v) => update({ action_timing: v })}>
                <SelectTrigger className="h-9 bg-white" data-testid="ygg-mgr-timing"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent className="bg-white">{TIMING.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Row label="Kimi Yedekliyor">
              <Input value={ev.backs_up_who || ""} onChange={(e) => update({ backs_up_who: e.target.value })} placeholder="Rol veya kişi" data-testid="ygg-mgr-backsup" />
            </Row>
            <Row label="Yedeği Kim">
              <Input value={ev.backup_person || ""} onChange={(e) => update({ backup_person: e.target.value })} placeholder="Rol veya kişi" data-testid="ygg-mgr-backup" />
            </Row>
            <Row label="Yedek Hazırlık Süresi">
              <Input value={ev.backup_readiness || ""} onChange={(e) => update({ backup_readiness: e.target.value })} placeholder="Örn: 6 ay" data-testid="ygg-mgr-readiness" />
            </Row>
            <Row label="T-Role">
              <Input value={ev.t_role || ""} onChange={(e) => update({ t_role: e.target.value })} placeholder="Teknik/Team Role" data-testid="ygg-mgr-trole" />
            </Row>
          </div>

          <Row label="Yönetici Notu">
            <Textarea rows={2} value={ev.notes || ""} onChange={(e) => update({ notes: e.target.value })} data-testid="ygg-mgr-notes" />
          </Row>

          <div className="flex items-center justify-end pt-3 border-t border-slate-100">
            <Button onClick={save} disabled={saving} data-testid="ygg-mgr-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Değerlendirmeyi Kaydet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SurveyItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function SurveyChips({ label, items, tone }) {
  if (!items || items.length === 0) return null;
  const cls = tone === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-amber-50 text-amber-800 border-amber-100";
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((s) => <span key={s} className={`px-2 py-0.5 rounded-full text-[11px] border ${cls}`}>{s}</span>)}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
